import { Component, Inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { AbstractControl, FormArray, FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogModule} from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatExpansionModule } from '@angular/material/expansion';
import { DataModelSchemaTable, DataModelSchemaField, TableConversionMapping, FieldConversionMapping, ConditionMapping, FieldReplace, SwitchConversionMapping, CumulativeOptions, SequenceMapping, LinkConversionMapping, ClientPayloadService } from '../../../../services/client-payload-service';
import { MatSelectModule } from '@angular/material/select';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { HttpService } from '../../../utility/http-service';
import { DialogService } from '../../../utility/dialog/dialog-service';
import { ModalType } from '../../../utility/dialog-template/dialog-template.component';
export interface MappingEditorData {
  dataSetId: string;
  dataSetName: string;
  externalDataModel: string;
}
export type MappingEditorMode = 'field' | 'groupBy';


@Component({
  selector: 'app-mapping-editor',
  standalone: true,
  templateUrl: './mapping-editor.component.html',
  styleUrls: ['./mapping-editor.component.css'],
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatButtonModule,
    MatInputModule,
    MatCheckboxModule,
    MatSelectModule,
    MatAutocompleteModule,
    MatFormFieldModule,
    MatExpansionModule,
  ],
})

export class MappingEditorComponent implements OnInit {
  form!: FormGroup;
  loading = true;
  saving = false;
  error: string | null = null;
  targetTables: DataModelSchemaTable[] = [];
  sourceTables: DataModelSchemaTable[] = [];

  /** Expansion state per table mapping, kept in sync (by index) with tableMappings.controls. */
  expandedFlags: boolean[] = [];

  hasChanges = false;
  private savedSnapshot = '';

  private conditionOriginals = new WeakMap<AbstractControl, ConditionMapping>();
  private fieldConversionOriginals = new WeakMap<AbstractControl, FieldConversionMapping>();
  private fragmentOriginals = new WeakMap<AbstractControl, FieldConversionMapping>();
  private switchOriginals = new WeakMap<AbstractControl, SwitchConversionMapping>();
  constructor(
    private fb: FormBuilder,
    private dialogService: DialogService,
    private payloadService: ClientPayloadService,
    private http: HttpService,
    public dialogRef: MatDialogRef<MappingEditorComponent>,
    @Inject(MAT_DIALOG_DATA) public data: MappingEditorData,
  ) { }


  ngOnInit(): void {
    this.form = this.fb.group({
      tableMappings: this.fb.array([])
    });

    let pending = 3;
    let existingMappings: TableConversionMapping[] = [];

    const checkDone = () => {
      pending--;
      if (pending === 0) {
        existingMappings.forEach(m => this.addTableMapping(m));
        this.savedSnapshot = this.buildPayload();
        this.form.valueChanges.subscribe(() => this.hasChanges = this.buildPayload() !== this.savedSnapshot);
        this.loading = false;
      }
    };

    this.payloadService.getDataModelSchema('WellChron', tables => {
      this.targetTables = tables;
      checkDone();
    }, () => {
      this.error = 'Failed to load WellChron schema.';
      checkDone();
    });

    this.payloadService.getDataModelSchema(this.data.externalDataModel, tables => {
      this.sourceTables = tables;

      console.log('SOURCE TABLES:', this.sourceTables);
      checkDone();
    }, () => {
      this.error = 'Failed to load source schema.';
      checkDone();
    });

    this.payloadService.getMappingPayload(this.data.dataSetId, raw => {

      console.log('RAW MAPPING PAYLOAD:', raw);
      console.log('the number of tables is: ', raw?.length);
      if (raw) {
        console.log('RAW length:', raw.length);
        console.log('RAW FromTable count:', (raw.match(/"FromTable"/g) || []).length);
        console.log('RAW ToTable count:', (raw.match(/"ToTable"/g) || []).length);
        console.log('RAW FieldConversions count:', (raw.match(/"FieldConversions"/g) || []).length);
        console.log('RAW first 400 chars:', raw.slice(0, 400));

        console.log(raw.search(/\S/));
        console.log(raw.trim().length);
        console.log(raw.slice(-200));
        try {
          const parsed = JSON.parse(raw);
          console.log('PARSED top-level length:', parsed.length);
          console.log('PARSED re-serialized length:', JSON.stringify(parsed).length);

          parsed.forEach((mapping: any, i: number) => {
            
          });

          existingMappings = parsed;
          console.log('PARSED MAPPING PAYLOAD', existingMappings)

           console.log('the number of tables is: ', existingMappings?.length);
        } catch (e) {
          console.error('FAILED TO PARSE MAPPING PAYLOAD:', e);
          this.error = 'Invalid mapping payload returned from server.';
        }
      } else {
        console.log('NO EXISTING MAPPING PAYLOAD');
        existingMappings = [];
      }

      checkDone();

    }, () => {
      this.error = 'Failed to load existing mapping.';
      checkDone();
    });
  }

  get tableMappings(): FormArray {
    return this.form.get('tableMappings') as FormArray;
  }


  addTableMapping(existing?: TableConversionMapping): void {
    const group = this.newTableMappingGroup(existing);
    if (existing) {
      // Loading a saved config: append so the original order is preserved.
      this.tableMappings.push(group);
      this.expandedFlags.push(false);
    } else {
      // Added via the button: insert at the top, expanded, so it's visible without scrolling.
      this.tableMappings.insert(0, group);
      this.expandedFlags.unshift(true);
    }
  }

  removeTableMapping(i: number): void {
    this.dialogService.openModal(
      'Confirm Deletion',
      `Are you sure you want to delete?`,
      ModalType.QUESTION,
      () => {
        this.tableMappings.removeAt(i);
        this.expandedFlags.splice(i, 1);
      },
      () => {
      }
    )
  }

  tableMappingSummary(tableMappingGroup: AbstractControl): string {
    const from = this.tableDisplayName(this.sourceTables, tableMappingGroup.get('fromTable')!.value);
    const to = this.tableDisplayName(this.targetTables, tableMappingGroup.get('toTable')!.value);

    // console.log(`${from} → ${to}`)
    return `${from} → ${to}`;
  }

  private tableDisplayName(tables: DataModelSchemaTable[], key: string): string {
    if (!key) return '(unset)';
    const table = tables.find(t => this.tableKey(t) === key);
    return table?.displayName ?? key.split('||')[1];
  }

  fieldConversionsOf(tableMappingGroup: AbstractControl): FormArray {
    return (tableMappingGroup as FormGroup).get('fieldConversions') as FormArray;
  }

  addFieldConversion(tableMappingGroup: AbstractControl, existing?: FieldConversionMapping): void {
    const arr = this.fieldConversionsOf(tableMappingGroup);
    const group = this.newFieldConversionGroup(existing);
    if (existing) {
      arr.push(group);
    } else {
      arr.insert(0, group);
    }
  }

  removeFieldConversion(tableMappingGroup: AbstractControl, i: number): void { 
    this.dialogService.openModal(
      'Confirm Deletion',
      `Are you sure you want to delete?`,
      ModalType.QUESTION,
      () => {
         this.fieldConversionsOf(tableMappingGroup).removeAt(i);
      },
      () => {
      }
    )
  }

  /** True when the conversion's value comes from Concatenate/Addition/Switches/FunctionFields rather than a plain FromField. */
  private usesAlternateValueSource(fc?: FieldConversionMapping): boolean {
    return !!(fc?.Concatenate?.length || fc?.Addition?.length || fc?.Switches?.length || fc?.FunctionFields?.length);
  }

  private newFieldConversionGroup(existing?: FieldConversionMapping, requireToField = true, requireFromField = true, mode: MappingEditorMode = 'field'): FormGroup {

    const group = this.fb.group({
      toField: [existing?.ToField ?? '', requireToField ? Validators.required : []],
      isConstant: [existing?.IsConstant ?? false],
      fromFieldOrConstant: [existing?.FromField ?? '', requireFromField ? Validators.required : []],
      sourceUnit: [existing?.SourceUnit ?? ''],
      prefix: [existing?.Prefix ?? ''],
      suffix: [existing?.Suffix ?? ''],
      maxChars: [existing?.MaxChars ?? null],
      divide: [existing?.Divide ?? null],
      multiply: [existing?.Multiply ?? null],
      inverse: [existing?.Inverse ?? false],
      isFraction: [existing?.IsFraction ?? false],
      ignoreUnits: [existing?.IgnoreUnits ?? false],
      linkChain: this.fb.array(this.parseLinkChain(existing)),
      blindJoin: [existing?.BlindJoin ?? false],
      childTable: [existing?.ChildTable ? `${existing.ChildSchema ?? 'dbo'}||${existing.ChildTable}` : ''],
      childAlias: [existing?.ChildAlias ?? ''],
      childAggregate: [existing?.ChildAggregate ?? 'None'],
      replace: this.fb.array((existing?.Replace ?? []).map(r => this.newReplaceGroup(r))),

      isFunction: [existing?.IsFunction ?? false],
      formatString: [existing?.FormatString ?? ''],
      expectedResult: [existing?.ExpectedResult ?? ''],
      castType: [existing?.CastType ?? ''],
      defaultValue: [existing?.DefaultValue ?? ''],
      isTime: [existing?.IsTime ?? false],
      reportStartHour: [existing?.ReportStartHour ?? 6],

      cumulativeEnabled: [!!existing?.CumulativeOrder],
      cumulativeSequenceField: [existing?.CumulativeOrder?.SequenceField ?? ''],
      cumulativeParentSchema: [existing?.CumulativeOrder?.ParentSchema ?? ''],
      cumulativeParentTable: [existing?.CumulativeOrder?.ParentTable ?? ''],
      cumulativeParentLinkAlias: [existing?.CumulativeOrder?.ParentLinkAlias ?? ''],

      sequenceEnabled: [!!existing?.SequenceMapping],
      sequencePartitionFields: [(existing?.SequenceMapping?.ParitionFields ?? []).join(', ')],
      sequenceOrderByFields: [(existing?.SequenceMapping?.OrderByFields ?? []).join(', ')],
      sequenceAscending: [existing?.SequenceMapping?.Ascending ?? true],

      concatenateDelimiter: [existing?.ConcatenateDelimiter ?? ''],
      concatenate: this.fb.array((existing?.Concatenate ?? []).map(f => this.newFragmentGroup(f))),
      addition: this.fb.array((existing?.Addition ?? []).map(f => this.newFragmentGroup(f))),
      functionFields: this.fb.array((existing?.FunctionFields ?? []).map(f => this.newFragmentGroup(f))),

      switches: this.fb.array((existing?.Switches ?? []).map(s => this.newSwitchGroup(s))),
      switchDefault: [existing?.SwitchDefault ?? ''],

      expanded: [!existing],
      uiSections: this.fb.group({
        text: [false],
        unitMath: [false],
        link: [false],
        childAggregate: [false],
        findReplace: [false],
        concatenate: [false],
        addition: [false],
        switches: [false],
        typeControl: [false],
        functionFields: [false],
        cumulative: [false],
        sequence: [false]
      })
    });

    if (existing) this.fieldConversionOriginals.set(group, existing);

    return group;
  }

  /** Builds one FormGroup per hop in an existing Link chain (flat LinkSchema/LinkTable/LinkAlias counts as a single hop). */
  private parseLinkChain(existing?: FieldConversionMapping): FormGroup[] {
    const hops: FormGroup[] = [];
    if (existing?.Link) {
      let node: LinkConversionMapping | undefined = existing.Link;
      while (node) {
        hops.push(this.newLinkHopGroup(node.LinkSchema, node.LinkTable, node.LinkAlias));
        node = node.Then;
      }
    } else if (existing?.LinkTable) {
      hops.push(this.newLinkHopGroup(existing.LinkSchema, existing.LinkTable, existing.LinkAlias));
    }
    return hops;
  }

  private newLinkHopGroup(schema?: string, table?: string, alias?: string): FormGroup {
    return this.fb.group({
      table: [table ? `${schema ?? 'dbo'}||${table}` : ''],
      alias: [alias ?? '']
    });
  }

  linkChainOf(fieldConversionGroup: AbstractControl): FormArray {
    return (fieldConversionGroup as FormGroup).get('linkChain') as FormArray;
  }

  addLinkHop(fieldConversionGroup: AbstractControl): void {
    this.linkChainOf(fieldConversionGroup).push(this.newLinkHopGroup());
  }

  removeLinkHop(fieldConversionGroup: AbstractControl, i: number): void {
    this.linkChainOf(fieldConversionGroup).removeAt(i);
  }

  private newReplaceGroup(existing?: FieldReplace): FormGroup {
    return this.fb.group({
      replace: [existing?.Replace ?? '', Validators.required],
      with: [existing?.With ?? '']
    });
  }

  replaceOf(fieldConversionGroup: AbstractControl): FormArray {
    return (fieldConversionGroup as FormGroup).get('replace') as FormArray;
  }

  addReplaceRow(fieldConversionGroup: AbstractControl): void {
    this.replaceOf(fieldConversionGroup).push(this.newReplaceGroup());
  }

  removeReplaceRow(fieldConversionGroup: AbstractControl, i: number): void {
    this.replaceOf(fieldConversionGroup).removeAt(i);
      
  }

  private splitCsv(value: string): string[] {
    return value.split(',').map(s => s.trim()).filter(s => s.length > 0);
  }

  private newFragmentGroup(existing?: FieldConversionMapping): FormGroup {
    const group = this.fb.group({
      fromFieldOrConstant: [existing?.FromField ?? ''],
      isConstant: [existing?.IsConstant ?? false],
      prefix: [existing?.Prefix ?? ''],
      suffix: [existing?.Suffix ?? '']
    });
    if (existing) this.fragmentOriginals.set(group, existing);
    return group;
  }

  fragmentsOf(fieldConversionGroup: AbstractControl, arrayName: 'concatenate' | 'addition' | 'functionFields'): FormArray {
    return (fieldConversionGroup as FormGroup).get(arrayName) as FormArray;
  }

  addFragment(fieldConversionGroup: AbstractControl, arrayName: 'concatenate' | 'addition' | 'functionFields'): void {
    this.fragmentsOf(fieldConversionGroup, arrayName).push(this.newFragmentGroup());
  }

  removeFragment(fieldConversionGroup: AbstractControl, arrayName: 'concatenate' | 'addition' | 'functionFields', i: number): void {
    
    this.dialogService.openModal(
      'Confirm Deletion',
      `Are you sure you want to delete?`,
      ModalType.QUESTION,
      () => {
        this.fragmentsOf(fieldConversionGroup, arrayName).removeAt(i);
      },
      () => {
      }
    )
  }

  private buildFragment(fragmentGroup: AbstractControl, frag: any): FieldConversionMapping {
    const original = this.fragmentOriginals.get(fragmentGroup);
    return {
      ...(original ?? {}),
      IsFunction: original?.IsFunction ?? false,
      FromField: frag.fromFieldOrConstant || undefined,
      IsConstant: frag.isConstant,
      Prefix: frag.prefix || undefined,
      Suffix: frag.suffix || undefined
    };
  }

  private buildFragmentList(fieldConversionGroup: AbstractControl, arrayName: 'concatenate' | 'addition' | 'functionFields', values: any[]): FieldConversionMapping[] | undefined {
    if (!values?.length) return undefined;
    const arr = this.fragmentsOf(fieldConversionGroup, arrayName);
    return values.map((frag, i) => this.buildFragment(arr.at(i), frag));
  }

  private isSwitchSupported(sw?: SwitchConversionMapping): boolean {
    if (!sw) return true;
    if (sw.From) return false;
    if (sw.FromCondition) return this.isConditionSupported(sw.FromCondition);
    return true;
  }

  private newSwitchGroup(existing?: SwitchConversionMapping): FormGroup {
    const supported = this.isSwitchSupported(existing);
    const usesCondition = !!existing?.FromCondition;

    const group = this.fb.group({
      unsupported: [!!existing && !supported],
      mode: [usesCondition ? 'condition' : 'field'],
      fromField: [existing?.FromField ?? ''],
      valueCondition: [existing?.ValueCondition ?? ''],
      conditionLogic: [existing?.FromCondition?.Type?.toLowerCase() === 'or' ? 'or' : 'and'],
      conditionRules: this.fb.array(supported ? this.parseConditionRules(existing?.FromCondition) : []),
      valueResult: [existing?.ValueResult ?? '', Validators.required],
      isConstant: [existing?.IsConstant ?? false]
    });

    if (existing) this.switchOriginals.set(group, existing);
    return group;
  }

  switchesOf(fieldConversionGroup: AbstractControl): FormArray {
    return (fieldConversionGroup as FormGroup).get('switches') as FormArray;
  }

  addSwitchRow(fieldConversionGroup: AbstractControl): void {
    this.switchesOf(fieldConversionGroup).push(this.newSwitchGroup());
  }

  removeSwitchRow(fieldConversionGroup: AbstractControl, i: number): void {
    this.switchesOf(fieldConversionGroup).removeAt(i);
  }

  onSwitchModeChange(switchGroup: AbstractControl, mode: string): void {
    if (mode === 'condition' && this.conditionRulesOf(switchGroup).length === 0) {
      this.addConditionRule(switchGroup);
    }
  }

  private buildSwitch(switchGroup: AbstractControl, sw: any): SwitchConversionMapping {
    const original = this.switchOriginals.get(switchGroup);
    if (sw.unsupported) return original!;

    const base = {
      ...(original ?? {}),
      ValueResult: sw.valueResult,
      IsConstant: sw.isConstant
    };

    const ruleGroups = this.conditionRulesOf(switchGroup).controls;
    if (sw.mode === 'condition' && ruleGroups.length > 0) {
      return {
        ...base,
        FromCondition: this.rulesToConditionMapping(ruleGroups, sw.conditionLogic),
        // Required (non-nullable) on the backend but unused in condition mode.
        ValueCondition: '',
        FromField: undefined
      };
    }
    return {
      ...base,
      FromField: sw.fromField,
      ValueCondition: sw.valueCondition || '',
      FromCondition: undefined
    };
  }

  /** Collapsed-panel summary for one field mapping, e.g. "Elevation Casing Flange ← elvcasflange". */
  fieldMappingSummary(tableMappingGroup: AbstractControl, fieldConversionGroup: AbstractControl): string {
    const toField = fieldConversionGroup.get('toField')!.value;
    const toLabel = this.fieldDisplayName(this.targetFieldsFor(tableMappingGroup), toField) || '(unset)';

    const isConstant = fieldConversionGroup.get('isConstant')!.value;
    const fromValue = fieldConversionGroup.get('fromFieldOrConstant')!.value;
    const fromLabel = isConstant
      ? (fromValue ? `"${fromValue}"` : '(constant)')
      : (this.fieldDisplayName(this.fieldSourceOptionsFor(tableMappingGroup, fieldConversionGroup), fromValue) || '(unset)');
    // console.log(`${fromLabel} → ${toLabel}`)
    return `${fromLabel} → ${toLabel}`;
  }

  private sectionsOf(fieldConversionGroup: AbstractControl): FormGroup {
    return (fieldConversionGroup as FormGroup).get('uiSections') as FormGroup;
  }

  isSectionExpanded(fieldConversionGroup: AbstractControl, section: string): boolean {
    return !!this.sectionsOf(fieldConversionGroup).get(section)?.value;
  }

  toggleSection(fieldConversionGroup: AbstractControl, section: string): void {
    const control = this.sectionsOf(fieldConversionGroup).get(section)!;
    control.setValue(!control.value);
  }

  /** True when a "more options" sub-section has anything actually set, so its header can be bolded. */
  isSectionConfigured(fieldConversionGroup: AbstractControl, section: string): boolean {
    const fc = fieldConversionGroup;
    switch (section) {
      case 'text':
        return !!(fc.get('prefix')?.value || fc.get('suffix')?.value || fc.get('maxChars')?.value);
      case 'unitMath':
        return !!(fc.get('sourceUnit')?.value || fc.get('divide')?.value || fc.get('multiply')?.value ||
          fc.get('inverse')?.value || fc.get('isFraction')?.value || fc.get('ignoreUnits')?.value);
      case 'link':
        return this.linkChainOf(fc).length > 0 || !!fc.get('blindJoin')?.value;
      case 'childAggregate': {
        const childAggregate = fc.get('childAggregate')?.value;
        return !!(fc.get('childTable')?.value || fc.get('childAlias')?.value ||
          (childAggregate && childAggregate !== 'None'));
      }
      case 'findReplace':
        return this.replaceOf(fc).length > 0;
      case 'concatenate':
        return this.fragmentsOf(fc, 'concatenate').length > 0;
      case 'addition':
        return this.fragmentsOf(fc, 'addition').length > 0;
      case 'switches':
        return this.switchesOf(fc).length > 0;
      case 'typeControl': {
        const reportStartHour = fc.get('reportStartHour')?.value;
        return !!(fc.get('isFunction')?.value || fc.get('isTime')?.value || fc.get('formatString')?.value ||
          fc.get('expectedResult')?.value || fc.get('castType')?.value || fc.get('defaultValue')?.value ||
          (reportStartHour && reportStartHour !== 6));
      }
      case 'functionFields':
        return this.fragmentsOf(fc, 'functionFields').length > 0;
      case 'cumulative':
        return !!fc.get('cumulativeEnabled')?.value;
      case 'sequence':
        return !!fc.get('sequenceEnabled')?.value;
      default:
        return false;
    }
  }

  private fieldDisplayName(fields: DataModelSchemaField[], fieldName: string): string {
    if (!fieldName) return '';
    return fields.find(f => f.fieldName === fieldName)?.displayName ?? fieldName;
  }

  /** Read-only hint showing the unit the selected "To Field" expects, e.g. "ft". */
  targetFieldUnit(tableMappingGroup: AbstractControl, fieldConversionGroup: AbstractControl): string {
    const toField = fieldConversionGroup.get('toField')!.value;
    return this.targetFieldsFor(tableMappingGroup).find(f => f.fieldName === toField)?.unitName ?? '';
  }

  tableKey(table: DataModelSchemaTable): string {
    return `${table.schemaName}||${table.tableName}`;
  }

  private getFieldsForTable(tables: DataModelSchemaTable[], key: string): DataModelSchemaField[] {
    const table = tables.find(t => this.tableKey(t) === key);
    return table?.fields ?? [];
  }

  targetFieldsFor(tableMappingGroup: AbstractControl): DataModelSchemaField[] {
    const key = tableMappingGroup.get('toTable')!.value;
    return this.getFieldsForTable(this.targetTables, key);
  }

  sourceFieldsFor(tableMappingGroup: AbstractControl): DataModelSchemaField[] {
    const key = tableMappingGroup.get('fromTable')!.value;
    return this.getFieldsForTable(this.sourceTables, key);
  }

  fieldSourceOptionsFor(tableMappingGroup: AbstractControl, fieldConversionGroup: AbstractControl): DataModelSchemaField[] {
    const childTableKey = fieldConversionGroup.get('childTable')?.value;
    if (childTableKey) {
      return this.getFieldsForTable(this.sourceTables, childTableKey);
    }
    const hops = this.linkChainOf(fieldConversionGroup).controls;
    const lastHopKey = hops.length ? hops[hops.length - 1].get('table')!.value : '';
    if (lastHopKey) {
      return this.getFieldsForTable(this.sourceTables, lastHopKey);
    }
    return this.sourceFieldsFor(tableMappingGroup);
  }

  private isConditionSupported(condition?: ConditionMapping): boolean {
    if (!condition) return true;
    if (condition.ChildConditions?.length) {
      return condition.ChildConditions.every(c => !c.ChildConditions?.length);
    }
    return true;
  }

  private newConditionRuleGroup(existing?: ConditionMapping): FormGroup {
    return this.fb.group({
      leftMode: [existing?.LeftField ? 'expression' : 'field'],
      left: [existing?.Left ?? ''],
      leftField: this.newFieldConversionGroup(existing?.LeftField, false, false),
      rightMode: [existing?.RightField ? 'expression' : 'field'],
      right: [existing?.Right ?? ''],
      rightField: this.newFieldConversionGroup(existing?.RightField, false, false),
      op: [existing?.Type ?? 'eq', Validators.required],
      isConstant: [existing?.IsConstant ?? true]
    });
  }

  private parseConditionRules(condition?: ConditionMapping): FormGroup[] {
    if (!condition) return [];
    if (condition.ChildConditions?.length) {
      return condition.ChildConditions.map(c => this.newConditionRuleGroup(c));
    }
    return [this.newConditionRuleGroup(condition)];
  }

  private newTableMappingGroup(existing?: TableConversionMapping): FormGroup {
    const conditionSupported = this.isConditionSupported(existing?.Condition);

    const group = this.fb.group({
      fromTable: [existing ? this.resolveTableKey(this.sourceTables, existing.FromSchema ?? 'dbo', existing.FromTable): '', Validators.required],
      toTable: [existing ? `${existing.ToSchema}||${existing.ToTable}` : '', Validators.required],
      distinct: [existing?.Distinct ?? false],
      conditionEnabled: [!!existing?.Condition],
      conditionUnsupported: [!!existing?.Condition && !conditionSupported],
      conditionLogic: [existing?.Condition?.Type?.toLowerCase() === 'or' ? 'or' : 'and'],
      conditionRules: this.fb.array(this.parseConditionRules(conditionSupported ? existing?.Condition : undefined)),
      groupBy: this.fb.array((existing?.GroupBy ?? []).map(gb => this.newFieldConversionGroup(gb, false, true, 'groupBy'))),
      fieldConversions: this.fb.array((existing?.FieldConversions ?? []).map(fc =>
        this.newFieldConversionGroup(fc, true, !this.usesAlternateValueSource(fc))
      )),
      conditionExpanded: [false],
      fieldMappingsExpanded: [false],
      groupByExpanded: [false]
    });

    if (existing?.Condition && !conditionSupported) {
      this.conditionOriginals.set(group, existing.Condition);
    }

    group.get('fromTable')!.valueChanges.subscribe(() => this.fieldConversionsOf(group).clear());
    group.get('toTable')!.valueChanges.subscribe(() => this.fieldConversionsOf(group).clear());

    return group;
  }

  private resolveTableKey(
  tables: DataModelSchemaTable[],
  schemaName: string,
  tableName: string
  ): string {
  const table = tables.find(
    t =>
      t.schemaName.toLowerCase() === schemaName.toLowerCase() &&
      t.tableName.toLowerCase() === tableName.toLowerCase()
  );

  return table ? this.tableKey(table) : `${schemaName}||${tableName}`;
  }

  conditionRulesOf(tableMappingGroup: AbstractControl): FormArray {
    return (tableMappingGroup as FormGroup).get('conditionRules') as FormArray;
  }

  addConditionRule(tableMappingGroup: AbstractControl): void {
    this.conditionRulesOf(tableMappingGroup).push(this.newConditionRuleGroup());
  }

  removeConditionRule(tableMappingGroup: AbstractControl, i: number): void {
    this.conditionRulesOf(tableMappingGroup).removeAt(i);
  }

  onConditionEnabledChange(tableMappingGroup: AbstractControl, checked: boolean): void {
    if (checked && this.conditionRulesOf(tableMappingGroup).length === 0) {
      this.addConditionRule(tableMappingGroup);
    }
  }

  groupByOf(tableMappingGroup: AbstractControl): FormArray {
    return (tableMappingGroup as FormGroup).get('groupBy') as FormArray;
  }

  addGroupByRow(tableMappingGroup: AbstractControl): void {
  this.groupByOf(tableMappingGroup).insert(
    0,
    this.newFieldConversionGroup(
      undefined,
      false,
      true,
      'groupBy'
    )
  );
  }
  removeGroupByRow(tableMappingGroup: AbstractControl, i: number): void {
    this.dialogService.openModal(
      'Confirm Deletion',
      `Are you sure you want to delete?`,
      ModalType.QUESTION,
      () => {
        this.groupByOf(tableMappingGroup).removeAt(i);
      },
      () => {
      }
    )
  }

  removeMapping(mappings: FormArray, index: number): void {
    this.dialogService.openModal(
      'Confirm Deletion',
      `Are you sure you want to delete?`,
      ModalType.QUESTION,
      () => {
         mappings.removeAt(index); 
      },
      () => {
      }
    )
  }


  private buildConditionRule(ruleGroup: AbstractControl): ConditionMapping {
    const r = ruleGroup.value;
    const result: ConditionMapping = {
      Type: r.op,
      // IsConstant defaults to true in the backend model, so only emit it when explicitly false.
      IsConstant: r.isConstant === false ? false : undefined
    };

    if (r.leftMode === 'expression') {
      result.LeftField = this.buildFieldConversion((ruleGroup as FormGroup).get('leftField')!, []);
    } else {
      result.Left = r.left;
    }

    if (r.rightMode === 'expression') {
      result.RightField = this.buildFieldConversion((ruleGroup as FormGroup).get('rightField')!, []);
    } else {
      result.Right = r.right;
    }

    return result;
  }

  private rulesToConditionMapping(ruleGroups: AbstractControl[], logic: string): ConditionMapping {
    if (ruleGroups.length === 1) return this.buildConditionRule(ruleGroups[0]);
    return { Type: logic, ChildConditions: ruleGroups.map(g => this.buildConditionRule(g)) };
  }

  private buildCondition(tableMappingGroup: AbstractControl, tm: any): ConditionMapping | undefined {
    if (!tm.conditionEnabled) return undefined;
    if (tm.conditionUnsupported) return this.conditionOriginals.get(tableMappingGroup);
    const ruleGroups = this.conditionRulesOf(tableMappingGroup).controls;
    if (ruleGroups.length === 0) return undefined;
    return this.rulesToConditionMapping(ruleGroups, tm.conditionLogic);
  }
  /** Builds a Link/Then chain from the hop rows. A single hop collapses to flat LinkSchema/LinkTable/LinkAlias. */
  private buildLinkChain(fcGroup: AbstractControl): LinkConversionMapping | undefined {
    const hops = this.linkChainOf(fcGroup).value as { table: string; alias: string }[];
    let node: LinkConversionMapping | undefined;
    for (let i = hops.length - 1; i >= 0; i--) {
      const [schema, table] = (hops[i].table || '').split('||');
      if (!table) continue; // incomplete hop — skip rather than emit a broken join
      node = { LinkSchema: schema || undefined, LinkTable: table, LinkAlias: hops[i].alias || undefined, Then: node };
    }
    return node;
  }

  private buildLinkFields(fcGroup: AbstractControl): Partial<FieldConversionMapping> {
    const chain = this.buildLinkChain(fcGroup);
    if (!chain) {
      return { Link: undefined, LinkSchema: undefined, LinkTable: undefined, LinkAlias: undefined };
    }
    if (chain.Then) {
      return { Link: chain, LinkSchema: undefined, LinkTable: undefined, LinkAlias: undefined };
    }
    return { LinkSchema: chain.LinkSchema, LinkTable: chain.LinkTable, LinkAlias: chain.LinkAlias, Link: undefined };
  }

  private buildFieldConversion(fcGroup: AbstractControl, targetFields: DataModelSchemaField[]): FieldConversionMapping {
    const fc = fcGroup.value;
    const original = this.fieldConversionOriginals.get(fcGroup);
    const targetField = targetFields.find(f => f.fieldName === fc.toField);

    return {
      // Spread first so any property with no dedicated control still survives untouched.
      ...(original ?? {}),
      // Required (non-nullable) on the backend — now a real control, so write the form value.
      IsFunction: fc.isFunction,
      FromField: fc.fromFieldOrConstant,
      ToField: fc.toField,
      IsConstant: fc.isConstant,
      SourceUnitSet: targetField?.unitSetName,
      SourceUnit: fc.sourceUnit || targetField?.unitName,
      Prefix: fc.prefix || undefined,
      Suffix: fc.suffix || undefined,
      MaxChars: fc.maxChars ?? undefined,
      Divide: fc.divide ?? undefined,
      Multiply: fc.multiply ?? undefined,
      Inverse: fc.inverse,
      IsFraction: fc.isFraction,
      IgnoreUnits: fc.ignoreUnits,
      BlindJoin: fc.blindJoin,
      ChildSchema: fc.childTable ? fc.childTable.split('||')[0] : undefined,
      ChildTable: fc.childTable ? fc.childTable.split('||')[1] : undefined,
      ChildAlias: fc.childAlias || undefined,
      ChildAggregate: fc.childAggregate !== 'None' ? fc.childAggregate : undefined,
      Replace: fc.replace?.length ? fc.replace.map((r: any) => ({ Replace: r.replace, With: r.with })) : undefined,

      FormatString: fc.formatString || undefined,
      ExpectedResult: fc.expectedResult || undefined,
      CastType: fc.castType || undefined,
      DefaultValue: fc.defaultValue || undefined,
      IsTime: fc.isTime,
      ReportStartHour: fc.reportStartHour ?? 6,

      CumulativeOrder: fc.cumulativeEnabled ? {
        SequenceField: fc.cumulativeSequenceField,
        ParentSchema: fc.cumulativeParentSchema || undefined,
        ParentTable: fc.cumulativeParentTable,
        ParentLinkAlias: fc.cumulativeParentLinkAlias || undefined
      } : undefined,

      SequenceMapping: fc.sequenceEnabled ? {
        ParitionFields: this.splitCsv(fc.sequencePartitionFields),
        OrderByFields: this.splitCsv(fc.sequenceOrderByFields),
        Ascending: fc.sequenceAscending
      } : undefined,

      Concatenate: this.buildFragmentList(fcGroup, 'concatenate', fc.concatenate),
      ConcatenateDelimiter: fc.concatenate?.length ? (fc.concatenateDelimiter || undefined) : undefined,
      Addition: this.buildFragmentList(fcGroup, 'addition', fc.addition),
      FunctionFields: this.buildFragmentList(fcGroup, 'functionFields', fc.functionFields),

      Switches: fc.switches?.length
        ? this.switchesOf(fcGroup).controls.map((sg: AbstractControl, i: number) => this.buildSwitch(sg, fc.switches[i]))
        : undefined,
      SwitchDefault: fc.switches?.length ? (fc.switchDefault || undefined) : undefined,

      ...this.buildLinkFields(fcGroup)
    };
  }

  private buildPayload(): string {
    const mappings: TableConversionMapping[] = this.tableMappings.controls.map((tmGroup) => {
      const tm = tmGroup.value;
      const [fromSchema, fromTable] = tm.fromTable.split('||');
      const [toSchema, toTable] = tm.toTable.split('||');
      const targetFields = this.getFieldsForTable(this.targetTables, tm.toTable);

      return {
        FromSchema: fromSchema,
        FromTable: fromTable,
        ToSchema: toSchema,
        ToTable: toTable,
        Distinct: tm.distinct,
        Condition: this.buildCondition(tmGroup, tm),
        // Same shape, same builder as FieldConversions — GroupBy entries are just FieldConversionMapping too.
        GroupBy: this.groupByOf(tmGroup).controls.map((gbGroup) =>
          this.buildFieldConversion(gbGroup, targetFields)
        ),
        FieldConversions: this.fieldConversionsOf(tmGroup).controls.map((fcGroup) =>
          this.buildFieldConversion(fcGroup, targetFields)
        )
      };
    });

    return JSON.stringify(mappings);
  }

  save(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    this.saving = true;
    this.error = null;

    this.payloadService.saveMappingPayload(this.data.dataSetId, this.buildPayload(), () => {
      this.dialogRef.close(true);
    }, () => {
      this.error = 'Save failed.';
      this.saving = false;
    });
  }

  clear(): void {
    this.payloadService.saveMappingPayload(this.data.dataSetId, null, () => {
      this.dialogRef.close(true);
    }, () => {
      this.error = 'Clear failed.';
    });
  }
}
